/**
 * Names this browser tab to the API, so a change this tab makes can be told apart from one made elsewhere
 * (CLIENTS_CROSS_USER_UPDATES_TEST_CASES.md).
 *
 * Every request to the app's own API carries `X-Vf-Tab-Id` (BrowserTabHeaderInterceptor). A client save sends the
 * SignalR push `getClientUpdate` to the whole workspace with that id as `originTabId`, and this tab drops the echo:
 * it already patched its own rows. Every other tab, the same user's included, still gets the push and shows
 * "Updates available".
 *
 * No Angular imports, so the spec runs under plain jasmine.
 */

/** Must match `ClientChangePublisher.OriginTabHeader` on the server. */
export const BROWSER_TAB_HEADER = 'X-Vf-Tab-Id';

/**
 * Made once per page load and never stored: a reload is a new tab, and a duplicated tab (which copies
 * sessionStorage) must not share its original's id.
 */
export const browserTabId: string = createTabId();

/**
 * Whether a request to `url` names this tab: only requests to the app's own API. Graph, Blob storage and every
 * other host are left alone, because their CORS rules would reject an unknown header.
 */
export function sendsTabHeader(url: string, apiBaseUrl: string | null | undefined): boolean {
    if (!url || !apiBaseUrl) {
        return false;
    }
    const base = apiBaseUrl.replace(/\/+$/, '');
    return url === base || url.startsWith(base + '/');
}

/** Whether a push names this tab as the one that made the change. */
export function isFromThisTab(originTabId: string | null | undefined): boolean {
    return !!originTabId && originTabId === browserTabId;
}

/** 32 hex characters, the shape the server accepts. */
function createTabId(): string {
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
    } else {
        bytes.forEach((_, i) => (bytes[i] = Math.floor(Math.random() * 256)));
    }
    return Array.from(bytes, (b) => ('0' + b.toString(16)).slice(-2)).join('');
}
